//C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Components\Survey\Pages\AssessmentManagement\SurveyList.jsx

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Plus,
  Search,
  Filter,
  FileText,
  Users,
  BarChart3,
  Pencil,
  Mail,
  MoreVertical,
  Monitor,
  PlusCircle,
  UserCog,
  Activity,
  Clock,
  CheckCircle2,
  XCircle,
  FileEdit,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import CreateSurveyModal from "../../Modal/CreateSurveyModal";
import { getSurveyDetails, publishAssessment, duplicateSurvey, createAssessmentTemplate } from "../../Services/SurveyService";
import { isAuthRedirectInProgress } from "../../../../Utility/authSession";
import image from "../../../../assests/NewLoginVectorImage1.png";
import { StatCardSkeleton, SurveyRowSkeleton } from "../Surveylistskeleton ";
import ViewSurveyModal from "../../Modal/ViewSurveyModal";
import PublishResponderModel from "../../Modal/PublishResponderModel";
import EditSurveyModal from "../../Modal/EditSurveyModal";
import SendSurveyMailModal from "../../Modal/SendSurveyMailModal";
import { sanitizeHtml, stripHtml, DESCRIPTION_RICH_CONTENT_STYLES } from "../../../../Utility/sanitizeHtml";

const STATUS_TABS = [
  { key: "Active", label: "Active", icon: Activity },
  { key: "Scheduled", label: "Scheduled", icon: Clock },
  { key: "Completed", label: "Completed", icon: CheckCircle2 },
  { key: "Inactive", label: "Inactive", icon: XCircle },
  { key: "Draft", label: "Draft", icon: FileEdit },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const MENU_WIDTH = 176;
const MENU_HEIGHT_ESTIMATE = 134;

const formatTime12Hour = (hours, minutes) => {
  const hourNumber = Number(hours);
  const minuteNumber = Number(minutes);

  if (Number.isNaN(hourNumber) || Number.isNaN(minuteNumber)) return "";

  const meridiem = hourNumber >= 12 ? "PM" : "AM";
  const displayHour = hourNumber % 12 || 12;

  return `${String(displayHour).padStart(2, "0")}:${String(minuteNumber).padStart(2, "0")} ${meridiem}`;
};

const formatDateParts = (value) => {
  if (!value) return { date: "--", time: "" };

  const normalizedValue = String(value).trim();
  const directMatch = normalizedValue.match(
    /^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/
  );

  if (directMatch) {
    const [, day, month, year, hours = "", minutes = ""] = directMatch;
    return {
      date: `${day}-${month}-${year}`,
      time: hours ? formatTime12Hour(hours, minutes) : "",
    };
  }

  const parsed = new Date(normalizedValue);
  if (Number.isNaN(parsed.getTime())) {
    return { date: normalizedValue, time: "" };
  }

  return {
    date: [
      String(parsed.getDate()).padStart(2, "0"),
      String(parsed.getMonth() + 1).padStart(2, "0"),
      parsed.getFullYear(),
    ].join("-"),
    time: formatTime12Hour(parsed.getHours(), parsed.getMinutes()),
  };
};

const getPageNumbers = (current, total) => {
  const pages = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "...") {
      pages.push("...");
    }
  }
  return pages;
};

const normalizeStatus = (status) => {
  if (status === "Incomplete") return "Draft";
  if (status === "Scheduled to start") return "Scheduled";
  if (status === "Survey Ended") return "Completed";
  return status;
};

export const TREND_PATHS = {
  volatile: "M 0,16 C 15,16 15,4 30,8 C 45,12 45,28 60,24 C 75,20 75,6 90,10 C 105,14 105,24 120,20",

  decreasing: "M 0,4 C 20,4 30,12 40,14 C 60,18 70,26 90,24 C 105,22 110,30 120,28",

  increasing: "M 0,28 C 20,28 30,20 40,18 C 60,14 70,6 90,8 C 105,10 110,2 120,4",

  dipAndRecover: "M 0,12 C 20,12 30,28 60,28 C 90,28 100,10 120,8",

  default: "M 0,25 C 15,25 20,21 32,23 C 45,25 52,15 65,17 C 78,19 82,9 95,11 C 105,13 112,19 120,16"
};

const MiniSparkline = ({ id, color, pathData = TREND_PATHS.default }) => {
  return (
    <svg viewBox="0 0 120 32" className="w-full h-8 mt-3" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`sparkline-grad-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      <path
        d={`${pathData} L 120,32 L 0,32 Z`}
        fill={`url(#sparkline-grad-${id})`}
      />

      <path
        d={pathData}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const StatCard = ({ id, icon: Icon, iconBg, iconColor, label, value, subtitle, sparklineColor, pathData }) => (
  <div className="bg-white rounded-2xl px-4 py-4 shadow-md border border-gray-100">
    <div className="flex items-start gap-3">
      <div className={`${iconBg} p-2.5 rounded-xl shrink-0`}>
        <Icon className={iconColor} size={20} />
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-900">{label}</p>
        <h2 className="text-2xl font-bold text-gray-900 mt-0.5">{value}</h2>
        <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
      </div>
    </div>
    <MiniSparkline id={id} color={sparklineColor} pathData={pathData} />
  </div>
);

const SurveyList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("Active");
  const [openCreateSurvey, setOpenCreateSurvey] = useState(false);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [mailSurvey, setMailSurvey] = useState(null);
  const [viewSurvey, setViewSurvey] = useState(null);
  const [responderSurvey, setResponderSurvey] = useState(null);
  const [TotalAssessmentCount, setTotalAssessmentCount] = useState(0)
  const [OverAllResponderCount, setOverAllResponderCount] = useState(0)
  const [editSurvey, setEditSurvey] = useState(null);
  const menuRef = useRef(null);
  const menuButtonRefs = useRef({});
  const filteredSurveys = surveys.filter((survey) => {
    const status = normalizeStatus(survey.status);

    return (
      status === activeTab &&
      survey.surveyName.toLowerCase().includes(search.toLowerCase())
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "Published":
        return "bg-green-100 text-green-700";
      case "Active":
        return "bg-green-100 text-green-700";
      case "Scheduled to start":
        return "bg-purple-100 text-purple-700";
      case "Survey Ended":
        return "bg-blue-100 text-blue-700";
      case "Draft":
        return "bg-orange-100 text-orange-700";
      case "Incomplete":
        return "bg-yellow-100 text-yellow-700";
      case "Inactive":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getTabCount = (tabKey) => surveys.filter((s) => normalizeStatus(s.status) === tabKey).length;

  const activeCount = getTabCount("Active");
  const draftCount = getTabCount("Draft");

  const totalPages = Math.max(1, Math.ceil(filteredSurveys.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedSurveys = filteredSurveys.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    loadSurveys();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, search]);

  useEffect(() => {
    if (openMenuId === null) return;
    const handleClickOutside = (e) => {
      const menuEl = menuRef.current;
      const btnEl = menuButtonRefs.current[openMenuId];
      if ((menuEl && menuEl.contains(e.target)) || (btnEl && btnEl.contains(e.target))) {
        return;
      }
      setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  useEffect(() => {
    if (openMenuId === null) return;
    const close = () => setOpenMenuId(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [openMenuId]);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      const response = await getSurveyDetails();
      if (response.data.status.toLowerCase() === "success") {
        setSurveys(response.data.surveyDetails || []);
        setTotalAssessmentCount(response.data.totalRecords)
        setOverAllResponderCount(response.data.overAllResponses)
      } else {
        CustomToast("fail", response.data.message);
      }
    } catch (error) {
      if (!isAuthRedirectInProgress()) {
        CustomToast("fail", error.response?.data?.message || "Unable to load surveys.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePublishSurvey = async (survey, payload) => {
    const response = await publishAssessment(payload);
    if (response.data.status.toLowerCase() !== "success") {
      CustomToast("fail", response.data.message || "Unable to publish assessment.");
      throw new Error(response.data.message || "Unable to publish assessment.");
    }
    CustomToast("success", "Assessment published successfully.");
    setViewSurvey(null);
    await loadSurveys();
  };

  const handleSendMail = async (payload) => {
    const requestBody = {
       assessmentID: String(mailSurvey?.surveyID), 
      templateTypeID: Number(payload.templateTypeID),
      subject: payload.subject,
      body: payload.body,
    };

    const response = await createAssessmentTemplate(requestBody);
    if (response.data.status?.toLowerCase() !== "success") {
      CustomToast("fail", response.data.message || "Unable to save mail template.");
      throw new Error(response.data.message);
    }
    CustomToast("success", "Mail template saved successfully.");
  };

  const handleDuplicateSurvey = async (surveyUID) => {
    const response = await duplicateSurvey(surveyUID);
    if (response.data.status?.toLowerCase() !== "success") {
      CustomToast("fail", response.data.message || "Unable to duplicate the assessment.");
      throw new Error(response.data.message || "Unable to duplicate the assessment.");
    }
    CustomToast("success", "Assessment duplicated successfully.");
    setViewSurvey(null);
    await loadSurveys();
  };

  const handleToggleMenu = (surveyId) => {
    if (openMenuId === surveyId) {
      setOpenMenuId(null);
      return;
    }
    const btn = menuButtonRefs.current[surveyId];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;

      setMenuPos({
        top:
          spaceBelow < MENU_HEIGHT_ESTIMATE
            ? rect.top - MENU_HEIGHT_ESTIMATE - 4
            : rect.bottom + 4,
        left: Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8),
      });
    }
    setOpenMenuId(surveyId);
  };
  const activeMenuSurvey = openMenuId !== null ? surveys.find((s) => s.surveyID === openMenuId) : null;

  return (
    <>
      <div className="min-h-screen bg-slate-50 p-0.5">
        <div className="max-w-[88rem] mx-auto">
          <div className="flex items-stretch gap-1.5 mb-2.5">
            <div className="hidden md:block w-72 lg:w-80 xl:w-96 shrink-0">
              <img src={image} loading="lazy" alt="Dashboard-Image" className="w-full h-full object-contain object-left" />
            </div>
            <div className="flex-1 flex flex-col gap-3 min-w-0 py-1">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Assessment List</h1>
                  <p className="text-gray-500 mt-1 text-sm lg:text-base">
                    Create, manage and analyze your Assessment in one place.
                  </p>
                </div>
                <button onClick={() => setOpenCreateSurvey(true)}
                  className="flex items-center gap-2 bg-[#7f35b2] hover:bg-[#3bb290] text-white px-4 py-2.5 rounded-lg text-sm shadow-sm shrink-0 self-start"
                >
                  <Plus size={16} />
                  Create Assessment
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mt-0.5">
                {loading ? <StatCardSkeleton /> : (
                  <StatCard
                    id="total-surveys"
                    icon={FileText}
                    iconBg="bg-[#7f35b2]/10"
                    iconColor="text-[#7f35b2]"
                    label="Total Assessment"
                    value={TotalAssessmentCount}
                    subtitle="All time Assessment"
                    sparklineColor="#7f35b2"
                    pathData={TREND_PATHS.default}
                  />
                )}
                {loading ? <StatCardSkeleton /> : (
                  <StatCard
                    id="active-surveys"
                    icon={BarChart3}
                    iconBg="bg-green-100"
                    iconColor="text-green-600"
                    label="Active"
                    value={activeCount}
                    subtitle="Currently active"
                    sparklineColor="#16a34a"
                    pathData={TREND_PATHS.volatile}
                  />
                )}
                {loading ? <StatCardSkeleton /> : (
                  <StatCard
                    id="survey-responses"
                    icon={Users}
                    iconBg="bg-blue-100"
                    iconColor="text-blue-600"
                    label="Total Responses"
                    value={OverAllResponderCount}
                    subtitle="Total responses"
                    sparklineColor="#2563eb"
                    pathData={TREND_PATHS.dipAndRecover}
                  />
                )}
                {loading ? <StatCardSkeleton /> : (
                  <StatCard
                    id="draft-surveys"
                    icon={Pencil}
                    iconBg="bg-orange-100"
                    iconColor="text-orange-600"
                    label="Draft"
                    value={draftCount}
                    subtitle="Not yet published"
                    sparklineColor="#ea580c"
                    pathData={TREND_PATHS.decreasing}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Status tabs + search + filter */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 mb-1.5 px-4 py-0.5">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
              <div className="flex items-center gap-1 overflow-x-auto">
                {STATUS_TABS.map(({ key, label, icon: Icon }) => {
                  const isActive = activeTab === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${isActive
                        ? "bg-[#7f35b2]/10 text-[#7f35b2]"
                        : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                        }`}
                    >
                      <Icon size={16} />
                      {label} ({getTabCount(key)})
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 py-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search surveys..."
                    className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm w-full sm:w-56 focus:outline-none focus:ring-1 focus:ring-[#7f35b2]"
                  />
                </div>

              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-gray-500">
                    <th className="px-4 py-3 font-medium min-w-[240px] w-[30%]">Assessment Details</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-center">Questions</th>
                    <th className="px-4 py-3 font-medium text-center">Responses</th>
                    <th className="px-4 py-3 font-medium">Created On</th>
                    <th className="px-4 py-3 font-medium">Last Updated</th>
                    <th className="px-4 py-3 font-medium">Shared By</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && Array.from({ length: 6 }).map((_, i) => <SurveyRowSkeleton key={i} />)}

                  {!loading && paginatedSurveys.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-16">
                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto bg-[#7f35b2]/10 rounded-full flex items-center justify-center">
                            <FileText className="text-[#7f35b2]" size={30} />
                          </div>
                          <h3 className="text-lg font-semibold mt-4">No Assessment Found</h3>
                          <p className="text-gray-500 mt-1 text-sm">
                            Create your first survey and start collecting responses.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    paginatedSurveys.map((survey) => {
                      const created = formatDateParts(survey.createDate);
                      const updated = formatDateParts(survey.updateDate);

                      return (
                        <tr
                          key={survey.surveyID}
                          className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-3">
                              <div className="bg-[#7f35b2]/10 p-2 rounded-lg shrink-0">
                                <FileText className="text-[#7f35b2]" size={18} />
                              </div>
                              <div>
                                <button
                                  type="button"
                                  onClick={() => setViewSurvey(survey)}
                                  className="text-left font-semibold text-gray-900 transition hover:text-[#7f35b2] hover:underline"
                                >
                                  {survey.surveyName}
                                </button>
                                <style>{DESCRIPTION_RICH_CONTENT_STYLES}</style>
                                <div
                                  className="description-rich-content text-gray-500 text-xs mt-0.5 line-clamp-2"
                                  title={stripHtml(survey.surveyDescription)}
                                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(survey.surveyDescription) }}
                                />
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                survey.status
                              )}`}
                            >
                              {survey.status === "Incomplete"
                                ? "Draft"
                                : survey.status === "Survey Ended"
                                ? "Assessment Ended"
                                : survey.status}
                              <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            </span>
                          </td>

                          <td className="px-4 py-3 text-center text-gray-700">{survey.totalQuestions}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{survey.totalSubmittedResponses ?? 0}</td>

                          <td className="px-4 py-3 text-gray-700">
                            <div>{created.date}</div>
                            {created.time && <div className="text-xs text-gray-400">{created.time}</div>}
                          </td>

                          <td className="px-4 py-3 text-gray-700">
                            <div>{updated.date}</div>
                            {updated.time && <div className="text-xs text-gray-400">{updated.time}</div>}
                          </td>

                          <td className="px-4 py-3 text-gray-700">
                            {survey.sharedBy || "--"}
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">

                              <button
                                onClick={() => setMailSurvey(survey)}
                                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-purple-600"
                                title="Send Mail"
                              >
                                <Mail size={16} />
                              </button>
                              {normalizeStatus(survey.status) === "Draft" && survey.canEdit !== false && (
                                <button
                                  onClick={() => setEditSurvey(survey)}
                                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-blue-600"
                                  title="Edit"
                                >
                                  <Pencil size={16} />
                                </button>
                              )}
                              <button
                                ref={(el) => (menuButtonRefs.current[survey.surveyID] = el)}
                                onClick={() => handleToggleMenu(survey.surveyID)}
                                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500"
                                title="More options"
                              >
                                <MoreVertical size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {!loading && filteredSurveys.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Showing {startIndex + 1} to {Math.min(startIndex + pageSize, filteredSurveys.length)} of{" "}
                  {filteredSurveys.length} surveys
                </p>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronsLeft size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  {getPageNumbers(currentPage, totalPages).map((page, idx) =>
                    page === "..." ? (
                      <span key={`ellipsis-${idx}`} className="px-1 text-gray-400 text-sm">
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium ${page === currentPage
                          ? "bg-[#7f35b2] text-white"
                          : "border border-gray-200 text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        {page}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronsRight size={16} />
                  </button>

                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-200 rounded-lg text-sm px-2 py-1.5 ml-1"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateSurveyModal isOpen={openCreateSurvey} onClose={() => setOpenCreateSurvey(false)} onSuccess={loadSurveys} />
      <ViewSurveyModal
        isOpen={viewSurvey !== null}
        survey={viewSurvey}
        onClose={() => setViewSurvey(null)}
        showPublish={activeTab === "Draft"}
        onPublish={handlePublishSurvey}
        onDuplicate={handleDuplicateSurvey}
        onMarkAsComplete={() => {
          setViewSurvey(null);
          loadSurveys();
        }}
      />
      <PublishResponderModel
        isOpen={responderSurvey !== null}
        survey={responderSurvey}
        onClose={() => setResponderSurvey(null)}
        onSuccess={async () => {
          await loadSurveys();
        }}
      />
      <EditSurveyModal
        isOpen={editSurvey !== null}
        survey={editSurvey}
        onClose={() => setEditSurvey(null)}
        onSuccess={loadSurveys}
      />
      <SendSurveyMailModal
        isOpen={mailSurvey !== null}
        survey={mailSurvey}
        onClose={() => setMailSurvey(null)}
        onSend={handleSendMail} 
        readOnly={normalizeStatus(mailSurvey?.status) === "Completed" || mailSurvey?.canEdit === false}
      />
      {activeMenuSurvey &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: menuPos.top, left: menuPos.left }}
            className="w-44 bg-white border border-gray-100 rounded-lg shadow-lg py-1 z-50"
          >
            <button
              onClick={() => {
                navigate("/dashboard/Assessment-List/Add-Questions", {
                  state: {
                    surveyUID: activeMenuSurvey.uniqueKey,
                    surveyName: activeMenuSurvey.surveyName,
                    surveyDescription: activeMenuSurvey.surveyDescription,
                    viewOnly: true,
                    surveyStatus: normalizeStatus(activeMenuSurvey.status),
                  },
                });
                setOpenMenuId(null);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-[#effaf6] hover:text-[#00845a]"
            >
              <Monitor size={14} />
              Preview
            </button>
            {normalizeStatus(activeMenuSurvey.status) === "Draft" && activeMenuSurvey.canEdit !== false && (
              <button
                onClick={() => {
                  navigate("/dashboard/Assessment-List/Add-Questions", {
                    state: {
                      surveyUID: activeMenuSurvey.uniqueKey,
                      surveyName: activeMenuSurvey.surveyName,
                      surveyDescription: activeMenuSurvey.surveyDescription,
                      surveyStatus: normalizeStatus(activeMenuSurvey.status),
                    },
                  });
                  setOpenMenuId(null);
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <PlusCircle size={14} />
                Manage Questions
              </button>
            )}

            {(normalizeStatus(activeMenuSurvey.status) === "Active" ||
              normalizeStatus(activeMenuSurvey.status) === "Scheduled") &&
              activeMenuSurvey.canEdit !== false && (
                <button
                  onClick={() => {
                    setResponderSurvey(activeMenuSurvey);
                    setOpenMenuId(null);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <UserCog size={14} />
                  Manage Responder
                </button>
              )}

            {(normalizeStatus(activeMenuSurvey.status) === "Active" ||
              normalizeStatus(activeMenuSurvey.status) === "Scheduled") &&
              activeMenuSurvey.canEdit !== false && (
                <button
                  onClick={() => {
                    navigate(
                      `/dashboard/Assessment-List/Add-Questions/build-analytics/${activeMenuSurvey.uniqueKey}`,
                      {
                      state: {
                        surveyUID: activeMenuSurvey.uniqueKey,
                        surveyName: activeMenuSurvey.surveyName,
                        surveyDescription: activeMenuSurvey.surveyDescription,
                        viewOnly: true,
                        unlockAnalytics: true,
                        surveyStatus: normalizeStatus(activeMenuSurvey.status),
                      },
                    });
                    setOpenMenuId(null);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <BarChart3 size={14} />
                  Manage Analytics
                </button>
              )} 


          </div>,
          document.body
        )}
    </>
  );
};

export default SurveyList;

